import { useDebouncedValue } from "@mantine/hooks"
import {
  Check,
  Checks,
  DotsThreeVertical,
  Hand,
  MagnifyingGlass,
  Phone,
  Prohibit,
  WhatsappLogo,
  XCircle,
} from "@phosphor-icons/react"
import {
  Avatar,
  CodenameTag,
  InputGroup,
  Menu,
  MenuDivider,
  MenuItem,
  Popover,
  Tag,
  Toast,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import classNames from "classnames"
import dayjs from "dayjs"
import { useState } from "react"
import toast from "react-hot-toast"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useClient } from "urql"
import { siteConfig } from "~/constants/config"
import { TradeKinds } from "~/constants/Kinds"
import { AppRoutes } from "~/constants/routes"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import {
  AddUpdateSharedEntityDocument,
  AddUpdateSharedEntityMutation,
  Order_By,
  SearchBaseUserIdDocument,
  SearchBaseUserIdQuery,
  useAddSfCodeMutation,
  useDemandEnquiriesQuery,
  useOfferEnquiriesQuery,
} from "~/generated/graphql"
import { EnquiryFilters } from "~/pages/d/demand-management/EnquiryFilters"
import { AddSFCodeModal } from "./AddSFCodeModal"
import { UserNotRegisteredModal } from "./UserNotRegisteredModal"

export const EnquiriesTable = ({
  pattiId,
  type,
  disableSaudaCreation,
  disableOrderCreation,
  status,
}: {
  pattiId: string
  type: "demand" | "offer"
  disableSaudaCreation?: boolean
  disableOrderCreation?: boolean
  status?: string
}) => {
  const [activeEnquiry, setActiveEnquiry] = useState<any>(null)
  const [showRegModal, setRegModal] = useState(false)
  const [params, setParams] = useSearchParams()
  const page = Number(params.get("page")) || 1
  const [search, setSearch] = useState(params.get("search") || "")
  const [debouncedSearch] = useDebouncedValue(search.trim(), 250)
  const [filter, setFilter] = useState<any>({})
  const isDemand = type === "demand"
  const navigate = useNavigate()

  const [{ data: demandData }, refetch] = useDemandEnquiriesQuery({
    variables: {
      limit: siteConfig.pageSize,
      offset: (page - 1) * siteConfig.pageSize,
      orderBy: {
        created_at: Order_By.Desc,
      },
      where: {
        whatsapp_status: {
          _eq: filter?.whatsapp_status?.name,
        },
        _or: [
          {
            seller: debouncedSearch
              ? {
                  name: {
                    _ilike: `%${debouncedSearch}%`,
                  },
                }
              : undefined,
          },
          {
            seller_external: debouncedSearch
              ? {
                  first_name: {
                    _ilike: `%${debouncedSearch}%`,
                  },
                }
              : undefined,
          },
          {
            seller_external: debouncedSearch
              ? {
                  last_name: {
                    _ilike: `%${debouncedSearch}%`,
                  },
                }
              : undefined,
          },
        ],
        demand_id: {
          _eq: pattiId,
        },
      },
    },
    pause: !pattiId || !type || type !== "demand",
    requestPolicy: "cache-and-network",
  })

  const [{ data: offerData }, offerRefetch] = useOfferEnquiriesQuery({
    variables: {
      limit: siteConfig.pageSize,
      offset: (page - 1) * siteConfig.pageSize,
      orderBy: {
        created_at: Order_By.Desc,
      },
      where: {
        whatsapp_status: {
          _eq: filter?.whatsapp_status?.name,
        },
        _or: [
          {
            buyer: debouncedSearch
              ? {
                  name: {
                    _ilike: `%${debouncedSearch}%`,
                  },
                }
              : undefined,
          },
          {
            buyer_external: debouncedSearch
              ? {
                  first_name: {
                    _ilike: `%${debouncedSearch}%`,
                  },
                }
              : undefined,
          },
          {
            buyer_external: debouncedSearch
              ? {
                  last_name: {
                    _ilike: `%${debouncedSearch}%`,
                  },
                }
              : undefined,
          },
        ],
        offer_id: {
          _eq: pattiId,
        },
      },
    },
    pause: !pattiId || !type || type !== "offer",
    requestPolicy: "cache-and-network",
  })
  const totalEnquiries = isDemand
    ? demandData?.demand_enquiries_aggregate?.aggregate?.count || 0
    : offerData?.offer_enquiries_aggregate?.aggregate?.count || 0
  const client = useClient()
  const [, addSFCode] = useAddSfCodeMutation()

  const disableAllActions = status === "closed"
  const disablePattiActions = status === "created"
  const COLUMNS = [
    {
      Header: "Date",
      accessor: "created_at",
      Cell({ value }: any) {
        return (
          <Cell
            title={dayjs(value).format("DD-MM-YYYY")}
            subtitle={dayjs(value).format("HH:mm A")}
          />
        )
      },
    },
    {
      Header: isDemand ? "Seller Name" : "Buyer Name",
      accessor: isDemand ? "seller" : "buyer",
      Cell({ value, row: { original } }: any) {
        const registered = isDemand ? original?.seller?.id : original?.buyer?.id
        const unRegisteredUser = isDemand
          ? original?.seller_external
          : original?.buyer_external
        const laterRegistered = unRegisteredUser?.user?.user?.[0]
        const sellerName = registered
          ? value?.user?.[0]?.name
          : unRegisteredUser?.first_name + " " + unRegisteredUser?.last_name
        return (
          <div className="flex flex-col">
            <span className="text-sm font-500">{sellerName}</span>
            {!registered && !laterRegistered && (
              <Tag
                intent="danger"
                size="sm"
                variant="minimal"
                className="w-fit px-1 !rounded-2xl"
              >
                Not Reg.
              </Tag>
            )}
            {(registered || laterRegistered) && (
              <CodenameTag
                className="mt-1"
                referenceId={
                  value?.user?.[0]?.external_reference_id2 ||
                  laterRegistered?.external_reference_id2
                }
              />
            )}
          </div>
        )
      },
    },
    {
      Header: "WhatsApp Status",
      accessor: "whatsapp_status",
      Cell({ value }: any) {
        const isSent = value === "Sent"
        const notSent = value === "Not Sent"
        const notInterested = value === "Not Interested"

        const isDelivered = value === "Delivered"
        const isInterested = value === "Interested"

        return (
          <div className="flex flex-col gap-1">
            {isInterested && <Hand size={18} className="text-yellow-500" />}
            {isSent && <Check size={18} className="text-gray-600" />}
            {isDelivered && <Checks size={18} className="text-blue-300" />}
            {notInterested && <Prohibit size={18} className="text-red" />}
            {notSent && <XCircle size={18} className="text-red" />}
            <span className="text-xs text-gray-600">{value}</span>
          </div>
        )
      },
    },
    // {
    //   Header: "Callback",
    //   accessor: "callback",
    //   Cell({ value }: any) {
    //     return <Cell title={value} />
    //   },
    // },
    {
      Header: "Placed By",
      accessor: `${isDemand ? "demand" : "offer"}.created_by`,
      Cell({ value, row: { original } }: any) {
        if (value === "-") return <Cell title="-" />
        return (
          <Tooltip
            content={
              <div>
                <p>{value?.full_name || "na"}</p>
                <p className="text-gray-400">
                  {dayjs(original?.created_at).format("DD MMM 'YY | h:mm a")}
                </p>
              </div>
            }
          >
            <Avatar
              className="relative text-center"
              name={value?.full_name}
              src={value?.photo?.url}
            />
          </Tooltip>
        )
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell({ value }: any) {
        const isPending = value === "Pending"
        const isHishobPatti = value === "Hishob Patti"
        const isNoResponse = value === "No Response"

        return (
          <Tag
            intent={isPending ? "info" : isHishobPatti ? "primary" : "none"}
            className={classNames(
              "px-2 py-0.5 text-[10px]",
              isPending
                ? "opacity-50"
                : isNoResponse
                ? "!text-white !bg-gray-500"
                : ""
            )}
          >
            {value}
          </Tag>
        )
      },
    },
    {
      Header: "Call",
      accessor: "call",
      Cell() {
        return (
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-full text-green-300 cursor-pointer shadow-sm">
              <Phone size={16} weight="fill" />
            </div>
          </div>
        )
      },
    },
    {
      Header: "",
      id: "actions",
      Cell({ row: { original } }: any) {
        const registered = isDemand ? original?.seller?.id : original?.buyer?.id
        const laterRegistered = isDemand
          ? original?.seller_external?.user?.user?.[0]
          : original?.buyer_external?.user?.user?.[0]
        const isRegistered = registered || laterRegistered
        return (
          <Popover
            minimal
            arrow={false}
            className="!p-0"
            trigger="click"
            interactive
            placement="bottom-end"
            content={
              <Menu className="max-h-fit min-w-[200px] !p-0">
                <div className="p-2">
                  <div className="font-600 ">Action</div>
                  <MenuItem
                    className=""
                    onClick={() => {
                      setActiveEnquiry(original)
                    }}
                    disabled={Boolean(registered) || disableAllActions}
                  >
                    Add SF Code
                  </MenuItem>
                </div>

                <MenuDivider className="!border-gray-300 !m-0" />

                <div className="p-2">
                  <div className="font-600">Create Patti</div>
                  <MenuItem
                    className=""
                    onClick={() =>
                      isRegistered
                        ? navigate(
                            `${AppRoutes.tradeRequests}/new?demandId=${pattiId}&type=${TradeKinds.SaudaPavti}&sellerId=${original?.seller?.id}`
                          )
                        : setRegModal(true)
                    }
                    hidden={!isDemand}
                    disabled={
                      disableSaudaCreation ||
                      disableAllActions ||
                      disablePattiActions
                    }
                  >
                    Sauda Patti
                  </MenuItem>
                  <MenuItem
                    className=""
                    onClick={() =>
                      isRegistered
                        ? navigate(
                            `${AppRoutes.tradeRequests}/new?offerId=${pattiId}&type=${TradeKinds.OrderPatti}&buyerId=${original?.buyer?.id}`
                          )
                        : setRegModal(true)
                    }
                    hidden={isDemand}
                    disabled={
                      disableOrderCreation ||
                      disableAllActions ||
                      disablePattiActions
                    }
                  >
                    Order Patti
                  </MenuItem>
                  {/* <MenuItem
                    className=""
                    onClick={() =>
                      isRegistered ? console.log("Storage") : setRegModal(true)
                    }
                  >
                    Storage Patti
                  </MenuItem> */}
                </div>
                <MenuDivider className="!border-gray-300 !m-0" />

                <div className="p-2" hidden>
                  <div className="font-600">WhatsApp</div>
                  <MenuItem onClick={() => {}} disabled={disableAllActions}>
                    <div className="flex items-center gap-1">
                      <WhatsappLogo size={18} className="text-green-500" />
                      Reply on WhatsApp
                    </div>
                  </MenuItem>
                </div>
              </Menu>
            }
          >
            <button className="p-1 rounded">
              <DotsThreeVertical size={20} />
            </button>
          </Popover>
        )
      },
    },
  ]

  return (
    <div className="bg-gray-100 border-1 border-gray-300 rounded-xl overflow-hidden mt-4">
      <header className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="font-600 text-gray-800">Enquiries Received</span>
          <span className="text-xs text-gray-700">{totalEnquiries} total</span>
        </div>
        <div className="flex items-center">
          <InputGroup
            className="w-full !rounded-r-none"
            leftElement={<MagnifyingGlass />}
            size="sm"
            inputProps={{
              value: search,
              placeholder: "Search",
              onChange(e) {
                const value = e.target.value
                setSearch(value)
                if (value) {
                  params.set("search", value)
                } else {
                  params.delete("search")
                }
                params.set("page", "1")
                setParams(params)
              },
            }}
          />
          <EnquiryFilters filter={filter} setFilter={setFilter} />
        </div>
      </header>
      <TableView
        columns={COLUMNS as any}
        data={
          isDemand
            ? demandData?.demand_enquiries || []
            : offerData?.offer_enquiries || []
        }
        isLoading={false}
        tableClassName="!min-h-[500px] overflow-y-scroll"
        getCellProps={() => ({ className: "!py-3 !border-b-gray-50" })}
        paginationProps={{
          total: Math.ceil(totalEnquiries / siteConfig.pageSize),
          page: page,
          onChange(page) {
            params.set("page", page.toString())
            params.set("search", "")
            setParams(params)
          },
        }}
      />

      <AddSFCodeModal
        isOpen={!!activeEnquiry}
        onClose={() => setActiveEnquiry(null)}
        onSubmit={async code => {
          if (!code) return
          // 1. Search for Base User by SF Code
          const searchResult = await client
            .query<SearchBaseUserIdQuery>(SearchBaseUserIdDocument, {
              query: code,
            })
            .toPromise()

          const baseUser = searchResult.data?.base_user?.[0]
          if (!baseUser) {
            return toast.error("User with this SF Code not found")
          }

          // 2. Upsert Shared Entity for the Base User
          const sharedEntityResult = await client
            .mutation<AddUpdateSharedEntityMutation>(
              AddUpdateSharedEntityDocument,
              {
                reference_id: baseUser.reference_id,
                group_name: SharedEntityGroup.KiteUser,
                name: baseUser.full_name,
              }
            )
            .toPromise()

          const sharedEntityId =
            sharedEntityResult.data?.insert_setu_trade_shared_entity_one?.id

          if (!sharedEntityId) {
            return toast.error("Failed to map user to shared entity")
          }

          // 3. Update Enquiry with the new Shared Entity ID
          const { error } = await addSFCode({
            baseUserEntityId: sharedEntityId,
            id: isDemand
              ? activeEnquiry?.seller_external?.id
              : activeEnquiry?.buyer_external?.id,
          })

          if (error) {
            return toast.custom(
              <Toast title={"Failed to update SF Code"} intent="danger" />
            )
          }
          toast.custom(<Toast title={"SF Code updated successfully"} />)
          if (isDemand) {
            refetch({
              requestPolicy: "network-only",
            })
          } else {
            offerRefetch({
              requestPolicy: "network-only",
            })
          }
          setActiveEnquiry(null)
        }}
      />

      <UserNotRegisteredModal
        isOpen={showRegModal}
        onClose={() => setRegModal(false)}
      />
    </div>
  )
}
